import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
  CircularProgress,
  IconButton,
  Divider,
  Collapse,
  FormControlLabel,
  Switch,
} from '@mui/material'
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid'
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  Close as CloseIcon,
  Save as SaveIcon,
} from '@mui/icons-material'
import { interventionConfigsAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import OverviewTab from '../components/interventions/OverviewTab'
import PathwaysTab from '../components/interventions/PathwaysTab'
import ExercisesTab from '../components/interventions/ExercisesTab'
import RiskCodesTab from '../components/interventions/RiskCodesTab'
import StrategiesTab from '../components/interventions/StrategiesTab'
import ContextRulesTab from '../components/interventions/ContextRulesTab'

// Status indicator styles
const statusStyles = {
  draft: { color: '#71717a', bg: '#f4f4f5', label: 'Draft' },
  active: { color: '#16a34a', bg: '#dcfce7', label: 'Active' },
  archived: { color: '#dc2626', bg: '#fee2e2', label: 'Archived' },
  template: { color: '#0284c7', bg: '#e0f2fe', label: 'Template' },
}

// Simple status indicator component
function StatusIndicator({ status, showLabel = true }) {
  const style = statusStyles[status] || statusStyles.draft
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: style.color,
        }}
      />
      {showLabel && (
        <Typography variant="body2" sx={{ color: style.color, fontSize: '0.8125rem' }}>
          {style.label}
        </Typography>
      )}
    </Box>
  )
}

function InterventionConfigs() {
  const { hasPermission, canManageBaseContent } = useAuth()
  const [configs, setConfigs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [configToDelete, setConfigToDelete] = useState(null)

  // Editor state
  const [selectedConfig, setSelectedConfig] = useState(null)
  const [editedConfig, setEditedConfig] = useState(null)
  const [isNew, setIsNew] = useState(false)
  const [saving, setSaving] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const [showArchived, setShowArchived] = useState(false)

  const canCreate = hasPermission('intervention_configs', 'create')
  const canUpdate = hasPermission('intervention_configs', 'update')
  const canDelete = hasPermission('intervention_configs', 'delete')

  const canEdit = useCallback(() => {
    if (!canUpdate) return false
    if (editedConfig?.is_base && !canManageBaseContent('intervention_configs')) return false
    return true
  }, [canUpdate, editedConfig, canManageBaseContent])

  const fetchConfigs = async () => {
    try {
      setLoading(true)
      const data = await interventionConfigsAPI.getAll({ includeArchived: 'true' })
      setConfigs(data)
      setError(null)
    } catch (err) {
      console.error('Error fetching intervention configs:', err)
      setError('Failed to load intervention configs')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchConfigs()
  }, [])

  // Auto-clear success message
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000)
      return () => clearTimeout(timer)
    }
  }, [successMessage])

  const handleCreate = () => {
    const newConfig = {
      code: '',
      name: '',
      description: '',
      target_population: '',
      status: 'draft',
      settings: {},
    }
    setSelectedConfig(null)
    setEditedConfig(newConfig)
    setIsNew(true)
    setIsDirty(false)
  }

  const handleSelect = async (config) => {
    // If clicking the same row, deselect
    if (selectedConfig?.id === config.id) {
      handleCloseEditor()
      return
    }

    try {
      // Fetch full config details
      const fullConfig = await interventionConfigsAPI.getById(config.id)
      setSelectedConfig(fullConfig)
      setEditedConfig(fullConfig)
      setIsNew(false)
      setIsDirty(false)
    } catch (err) {
      console.error('Error fetching intervention:', err)
      setError('Failed to load intervention details')
    }
  }

  const handleCloseEditor = () => {
    if (isDirty) {
      // Could add a confirmation dialog here
    }
    setSelectedConfig(null)
    setEditedConfig(null)
    setIsNew(false)
    setIsDirty(false)
  }

  const handleConfigChange = (field, value) => {
    setEditedConfig(prev => ({ ...prev, [field]: value }))
    setIsDirty(true)
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)

      if (isNew) {
        const created = await interventionConfigsAPI.create(editedConfig)
        setConfigs(prev => [...prev, created])
        setSelectedConfig(created)
        setEditedConfig(created)
        setIsNew(false)
        setSuccessMessage('Intervention created successfully')
      } else {
        const updated = await interventionConfigsAPI.update(selectedConfig.id, editedConfig)
        setConfigs(prev => prev.map(c => c.id === updated.id ? updated : c))
        setSelectedConfig(updated)
        setEditedConfig(updated)
        setSuccessMessage('Intervention saved successfully')
      }
      setIsDirty(false)
    } catch (err) {
      console.error('Error saving intervention:', err)
      setError(err.response?.data?.error || 'Failed to save intervention')
    } finally {
      setSaving(false)
    }
  }

  const handleDuplicate = async (config) => {
    try {
      const newConfig = await interventionConfigsAPI.duplicate(config.id, {
        name: `${config.name} (Copy)`,
        code: `${config.code}_copy_${Date.now()}`,
      })
      setConfigs([...configs, newConfig])
      // Select the new config
      setSelectedConfig(newConfig)
      setEditedConfig(newConfig)
      setIsNew(false)
      setIsDirty(false)
      setSuccessMessage('Intervention duplicated successfully')
    } catch (err) {
      console.error('Error duplicating config:', err)
      setError('Failed to duplicate intervention config')
    }
  }

  const handleDeleteClick = (config) => {
    setConfigToDelete(config)
    setDeleteConfirmOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!configToDelete) return

    try {
      await interventionConfigsAPI.delete(configToDelete.id)
      setConfigs(configs.filter(c => c.id !== configToDelete.id))
      // If we deleted the selected config, close editor
      if (selectedConfig?.id === configToDelete.id) {
        handleCloseEditor()
      }
      setDeleteConfirmOpen(false)
      setConfigToDelete(null)
      setSuccessMessage('Intervention archived successfully')
    } catch (err) {
      console.error('Error deleting config:', err)
      setError('Failed to delete intervention config')
    }
  }

  const canDeleteConfig = (config) => {
    if (!canDelete) return false
    if (config.is_base && !canManageBaseContent('intervention_configs')) return false
    return true
  }

  const columns = [
    {
      field: 'code',
      headerName: 'Code',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" fontFamily="monospace" color="text.secondary" fontSize="0.75rem">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={500}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'is_base',
      headerName: 'Source',
      width: 80,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
          {params.value ? 'Base' : 'Custom'}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => <StatusIndicator status={params.value} />,
    },
    {
      field: 'target_population',
      headerName: 'Target Population',
      flex: 1,
      minWidth: 180,
    },
    {
      field: 'track_count',
      headerName: 'Pathways',
      width: 80,
      type: 'number',
    },
    {
      field: 'enrollment_count',
      headerName: 'Enrolled',
      width: 80,
      type: 'number',
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      width: 80,
      getActions: (params) => {
        const actions = []

        if (canCreate) {
          actions.push(
            <GridActionsCellItem
              icon={<Tooltip title="Duplicate"><DuplicateIcon fontSize="small" /></Tooltip>}
              label="Duplicate"
              onClick={(e) => {
                e.stopPropagation()
                handleDuplicate(params.row)
              }}
            />
          )
        }

        if (canDeleteConfig(params.row)) {
          actions.push(
            <GridActionsCellItem
              icon={<Tooltip title="Archive"><DeleteIcon fontSize="small" /></Tooltip>}
              label="Archive"
              onClick={(e) => {
                e.stopPropagation()
                handleDeleteClick(params.row)
              }}
            />
          )
        }

        return actions
      },
    },
  ]

  const filteredConfigs = showArchived ? configs : configs.filter(c => c.status !== 'archived')
  const editorOpen = editedConfig !== null

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Intervention Programs</Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
            }
            label={<Typography variant="body2" color="text.secondary">Show archived</Typography>}
          />
          {canCreate && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreate}
              disabled={isNew}
            >
              New Intervention
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      <Paper sx={{ mb: 0 }}>
        <DataGrid
          rows={filteredConfigs}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10]}
          autoHeight
          disableSelectionOnClick
          hideFooterSelectedRowCount
          onRowClick={(params) => handleSelect(params.row)}
          getRowId={(row) => row.id}
          sx={{
            '& .MuiDataGrid-columnHeaders': {
              minHeight: '36px !important',
              maxHeight: '36px !important',
              lineHeight: '36px !important',
              fontSize: '0.75rem',
            },
            '& .MuiDataGrid-columnHeader': {
              height: '36px !important',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 600,
            },
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'action.hover',
            },
            '& .MuiDataGrid-row.Mui-selected': {
              backgroundColor: 'action.selected',
            },
            '& .MuiDataGrid-footerContainer': {
              minHeight: '32px !important',
              maxHeight: '32px !important',
              borderTop: 'none',
            },
            '& .MuiTablePagination-root': {
              overflow: 'hidden',
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-select, & .MuiTablePagination-selectIcon, & .MuiTablePagination-displayedRows': {
              display: 'none !important',
            },
            '& .MuiTablePagination-actions': {
              ml: 0,
            },
          }}
          selectionModel={selectedConfig ? [selectedConfig.id] : []}
        />
      </Paper>

      {/* Hint / Editor area */}
      {!editorOpen && (
        <Box
          sx={{
            py: 2,
            display: 'flex',
            justifyContent: 'center',
            color: 'text.disabled',
          }}
        >
          <Typography variant="body2">
            Click a row to view or edit
          </Typography>
        </Box>
      )}

      <Collapse in={editorOpen}>
        {editedConfig && (
          <Paper sx={{ mb: 2 }}>
            {/* Editor Header */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}
            >
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="h6">
                  {isNew ? 'New Intervention Program' : editedConfig.name}
                </Typography>
                {!isNew && <StatusIndicator status={editedConfig.status} />}
                {editedConfig.is_base && (
                  <Typography variant="caption" color="primary.main" fontWeight={500}>
                    Base Content
                  </Typography>
                )}
                {isDirty && (
                  <Typography variant="caption" color="warning.main" fontWeight={500}>
                    Unsaved
                  </Typography>
                )}
              </Box>
              <Box display="flex" gap={1} alignItems="center">
                {!isNew && canCreate && (
                  <Button
                    size="small"
                    startIcon={<DuplicateIcon />}
                    onClick={() => handleDuplicate(editedConfig)}
                    disabled={saving}
                  >
                    Duplicate
                  </Button>
                )}
                {canEdit() && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={saving || (!isDirty && !isNew)}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                )}
                <IconButton size="small" onClick={handleCloseEditor}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {/* Overview Section */}
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.6875rem', mb: 1.5 }}>
                Overview
              </Typography>
              <OverviewTab
                intervention={editedConfig}
                onChange={handleConfigChange}
                canEdit={canEdit()}
                isNew={isNew}
              />
            </Box>
            <Divider />

            {/* Pathways Section */}
            {!isNew && (
              <>
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.6875rem', mb: 1.5 }}>
                    Pathways
                  </Typography>
                  <PathwaysTab
                    interventionId={selectedConfig.id}
                    canEdit={canEdit()}
                    onError={setError}
                  />
                </Box>
                <Divider />
              </>
            )}

            {/* Exercises Section: picker categories + assigned activities */}
            {!isNew && (
              <>
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.6875rem', mb: 1.5 }}>
                    Exercises
                  </Typography>
                  <ExercisesTab
                    interventionId={selectedConfig.id}
                    canEdit={canEdit()}
                    onError={setError}
                  />
                </Box>
                <Divider />
              </>
            )}

            {/* Risk Codes Section */}
            {!isNew && (
              <>
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.6875rem', mb: 1.5 }}>
                    Risk Codes
                  </Typography>
                  <RiskCodesTab
                    interventionId={selectedConfig.id}
                    canEdit={canEdit()}
                    onError={setError}
                  />
                </Box>
                <Divider />
              </>
            )}

            {/* Patient Rules Section */}
            {!isNew && (
              <>
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.6875rem', mb: 1.5 }}>
                    Patient Rules
                  </Typography>
                  <ContextRulesTab
                    interventionId={selectedConfig.id}
                    canEdit={canEdit()}
                    onError={setError}
                  />
                </Box>
                <Divider />
              </>
            )}

            {/* Composition Rules Section (formerly Strategies) */}
            {!isNew && (
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.6875rem', mb: 1.5 }}>
                  Composition Rules
                </Typography>
                <StrategiesTab
                  interventionId={selectedConfig.id}
                  canEdit={canEdit()}
                  onError={setError}
                />
              </Box>
            )}

            {/* Help text for new interventions */}
            {isNew && (
              <Alert severity="info" sx={{ m: 2 }}>
                Save the intervention first to configure Pathways, Exercises, Risk Codes, Patient Rules, and Composition Rules.
              </Alert>
            )}
          </Paper>
        )}
      </Collapse>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Archive Intervention Program</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to archive "{configToDelete?.name}"?
            This will hide it from active use but preserve all associated data.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Archive
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default InterventionConfigs
